<template lang="html">
  <modal
    name="image-upload-modal"
    height="auto"
    width="768"
    top="120"
    :max-width="786"
    :adaptive="true"
    class="image-upload-modal"
    @before-open="beforeOpen"
    @before-close="beforeClose"
  >
    <FullScreenProgress :progress="progress" :max="120" />
    <div class="modal-container">
      <div class="padding-3x big-body bold">
        Product Photos
      </div>
      <div class="line-break-gray" />
      <div class="modal-body flex-box">
        <div class="carousel-container">
          <div class="carousel-placeholder" v-if="imageURLs.length == 0" />
          <ImageSlide
            ref="imageSlide"
            :imageURLs="imageURLs"
            :loading="loading"
          />
          <div class="gap-3x" />
          <div class="body-16">Upload up to 10 images for your product listing</div>
          <div class="gap-1x" />
          <div class="body-14 gray">Drag images to reorder how they appear in your image carousel</div>
          <div class="body-14 gray">Each image should be at least 1440px by 960px and maximum of {{maxFileSize}}MB</div>
          <FooterHelper />
        </div>
        <draggable
          v-model="imageURLs"
          class="image-cluster"
          draggable=".image-draggable"
          @end="updateImages"
        >
          <div class="image-preview image-draggable" v-for="(image, index) in imageURLs" :key="`${images[index]}_${index}`">
            <img :src="image" class="thumbnail" />
            <ControlIcon
              :imageURL="`${publicImagePath}/icon_close_white.png`"
              :activeImageURL="`${publicImagePath}/icon_close_white.png`"
              klass="delete-control"
              backgroundColor='#6D6D6D'
              hoverbackgroundColor='#D00000'
              @on-click="() => removeImage(imageURLs.indexOf(image))"
              v-if="!loading"
            />
          </div>
          <vue2Dropzone
            :awss3="awss3"
            id="image-upload"
            ref="imageUploadZone"
            v-on:vdropzone-s3-upload-error="s3UploadError"
            v-on:vdropzone-s3-upload-success="s3UploadSuccess"
            v-on:vdropzone-file-added="fileAdded"
            @vdropzone-upload-progress="updateProgress"
            :options="dropzoneOptions"
            :include-styling="false"
            :useCustomSlot="true"
            v-show="images.length < 10"
          >
            <OvalButton
              text="Add Image"
              klass="upload-button"
            />
          </vue2Dropzone>
          <div class="image-preview" v-for="i in emptySlots" :key="i">
          </div>
        </draggable>
      </div>
      <div class="line-break-gray" />
      <div class="modal-footer">
        <SymbiumButton
          text="Cancel"
          :onClick="close"
          :isSmall="true"
          :isDialog="true"
          kclass="cancel-btn"
        />
        <SymbiumButton
          text="Save"
          :onClick="save"
          :isSmall="true"
        />
      </div>
    </div>
  </modal>
</template>

<script>
import vue2Dropzone from 'vue2-dropzone'
import Draggable from 'vuedraggable'
import _ from 'lodash'

import OvalButton from 'src/components/common/OvalButton.vue'
import FullScreenProgress from 'src/components/common/FullScreenProgress.vue'
import ControlIcon from 'src/components/common/utility/ControlIcon.vue'
import ImageSlide from 'src/components/common/utility/ImageSlide.vue'
import FooterHelper from 'src/components/dashboard/shares/FooterHelper.vue'
import DisableBodyScroll from 'src/mixins/DisableBodyScroll.js'
import FileUploadProgress from "src/mixins/FileUploadProgress.js"
import SymbiumButton from 'src/components/common/SymbiumButton.vue'

export default {
  props: ['preupload'],
  components: {
    OvalButton, vue2Dropzone, Draggable, ControlIcon,
    FullScreenProgress, ImageSlide, FooterHelper, SymbiumButton
  },
  mixins: [DisableBodyScroll, FileUploadProgress],
  methods: {
    close() {
      this.$modal.hide('image-upload-modal');
    },
    beforeOpen(event) {
      this.images = _.clone(event.params.images)
      this.imageURLs = _.clone(event.params.imageURLs)
      this.designId = event.params.designId
      this.vendorId = event.params.vendorId
    },
    beforeClose(event) {
      if (this.fileUploadingInProgress) {
        event.stop()
      }
    },
    fileAdded(file) {
      var sizeInMB = (file.size / (1024*1024)).toFixed(2);
      if(sizeInMB > this.maxFileSize) {
        this.$modal.show('message-modal', {
          headerMessage: 'Error',
          bodyMessage: `File upload is limited to ${this.maxFileSize}MB. Your file was ${sizeInMB} MB.`
        })
        this.$refs.imageUploadZone.removeFile(file)
        return
      }
      this.images.push(file.name)
      const reader = new FileReader()
      reader.readAsDataURL(file)

      reader.onload = (event) => {
        this.imageURLs.push(event.target.result)
        this.$nextTick(() => {
          this.$refs.imageSlide.updateSlides()
        });
      }
    },
    updateImages(event) {
      if(event.oldIndex == event.newIndex) return;

      var movingItem = this.images[event.oldIndex]
      var topItems, bottomItems;

      if(event.oldIndex > event.newIndex) {
        topItems = this.images.slice(0, event.newIndex)
        bottomItems = this.images.slice(event.newIndex)
        bottomItems.splice(event.oldIndex - topItems.length, 1)
      } else {
        bottomItems = this.images.slice(event.newIndex + 1)
        topItems = this.images.slice(0, event.newIndex + 1)
        topItems.splice(event.oldIndex, 1)
      }
      this.images = topItems.concat([movingItem]).concat(bottomItems)
    },
    updateProgress(file, progress) {
      this.fileProgress[file.upload.uuid] = progress
      this.progress = _.sumBy(_.values(this.fileProgress)) / this.numberOfUploadingImages
    },
    s3UploadError() {
      alert("Image upload failed. Please try again or contact our support team.")
      this.loading = false
    },
    s3UploadSuccess() {
      if(this.$refs.imageUploadZone.getUploadingFiles().length == 0) {
        this.startHideProrgressBar()
        this.$emit('upload-finished', {
          designId: this.designId,
          images: this.images,
          imageURLs: this.imageURLs,
        })
        this.$modal.hide('image-upload-modal');
        this.loading = false
      }
    },
    save() {
      this.loading = true
      if (this.$refs.imageUploadZone.getQueuedFiles().length == 0) {
        this.loading = false
        this.$emit('upload-finished', {
          designId: this.designId,
          images: this.images,
          imageURLs: this.imageURLs,
          no_need_image_s3_processing: true,
        })
        this.$modal.hide('image-upload-modal');
        return;
      }
      if (this.designId) {
        this.startDisplayProgressBar(() => {
          this.numberOfUploadingImages = this.$refs.imageUploadZone.getQueuedFiles().length
          this.$refs.imageUploadZone.processQueue()
        })
      } else {
        this.startDisplayProgressBar(() => {
          this.$design.createDesign({vendorId: this.vendorId}, this.afterCreateDesignSuccessfully, this.afterCreateDesignFailed)
        })
      }
    },
    afterCreateDesignSuccessfully(design) {
      this.designId = design.data.designId
      this.$refs.imageUploadZone.setAWSSigningURL(`${process.env.VUE_APP_AUTH_API + '/s3_objects'}/design?designId=${this.designId}`)
      this.numberOfUploadingImages = this.$refs.imageUploadZone.getQueuedFiles().length
      this.$refs.imageUploadZone.processQueue()
    },
    afterCreateDesignFailed(e) {
      this.startHideProrgressBar()
      this.$emit('show-error', e)
      this.$modal.hide('image-upload-modal');
    },
    removeImage(index) {
      this.images.splice(index, 1)
      this.imageURLs.splice(index, 1)
      this.$nextTick(() => {
        this.$refs.imageSlide.updateSlides()
      })
    }
  },
  computed: {
    awss3() {
      return {
        signingURL: `${process.env.VUE_APP_AUTH_API + '/s3_objects'}/design?designId=${this.designId}`,
        withCredentials: true,
        sendFileToServer: false,
      }
    },
    emptySlots() {
      return Math.max(0, (9 - this.images.length));
    }
  },
  data() {
    return {
      images: [],
      imageURLs: [],
      designId: null,
      vendorId: null,
      loading: false,
      fileProgress: {},
      numberOfUploadingImages: 0,
      dropzoneOptions: {
        previewTemplate: '<div></div>',
        autoProcessQueue: false,
      },
      maxFileSize: 2,
    }
  }
}
</script>

<style lang="scss">
.image-upload-modal {
  .delete-control {
    top: -16px;
    right: -16px;
  }

  .modal-body {
    max-height: calc(100vh - 182px) !important;
  }

  .image-cluster {
    flex-wrap: wrap;
    display: flex;

    .image-preview {
      height: 72px;
      width: 108px;
      margin-left: 24px;
      margin-bottom: 24px;
      text-align: center;
      background-color: #EEEFFD;
      position: relative;

      img.thumbnail {
        height: 72px;
        object-fit: contain;
        width: 108px;
      }
    }
  }

  .footer-helper {
    border: none;
    padding: 0;
  }

  .upload-button {
    height: 72px;
    width: 108px;
    margin-left: 24px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    padding: 0;

    .text {
      width: 100%;
    }
  }

  .cancel-btn {
    margin-right: 16px !important;
  }

  .modal-body {
    padding-bottom: 0 !important;
  }

  .carousel-container {
    position: relative;
    min-width: 456px;

    .carousel-placeholder {
      background-color: #EEEFFD;
      width: 100%;
      height: 304px;

      @media screen and (max-width: 768px) {
        max-width: 100%;
        height: 240px;
      }
    }

    .footer-helper {
      position: absolute;
      bottom: 0;
      margin-bottom: 24px;
    }
  }
}
</style>
